import type { FormLayoutCells, FormLayoutRow, ValidationSchema } from '@/types';
import type { Data, ValidationField, Validation } from './user-form.types';
import { safeParse } from 'valibot';
import FormSchema from '@/utils/form-schema';

interface DataArgs<V> {
  layout: FormLayoutRow[];
  value: V;
  useIsDefault?: boolean;
}
export function data<V>({ layout, value, useIsDefault = false }: DataArgs<V>): Data<V> {
  return layout.reduce(
    (form, l) => ({
      ...form,
      ...l.cells.reduce((cell, c) => {
        if (useIsDefault && c.type === 'radio') {
          return { ...cell, [c.key]: c.choices.find((c) => c.isDefault)?.value || value };
        }
        return { ...cell, [c.key]: value };
      }, {}),
    }),
    {},
  );
}

export function keys({ layout }: { layout: FormLayoutRow[] }): string[] {
  return layout.reduce<string[]>((acc, row) => [...acc, ...row.cells.map((cell) => cell.key)], []);
}

export function labels({ layout }: { layout: FormLayoutRow[] }): Data<string> {
  return layout.reduce<Data<string>>((acc, row) => {
    row.cells.forEach((cell) => {
      const label = 'label' in cell && typeof cell.label === 'string' ? cell.label : row.label;
      acc[cell.key] = label;
    });
    return acc;
  }, {});
}

export function cells({ layout }: { layout: FormLayoutRow[] }): Data<FormLayoutCells> {
  return layout.reduce<Data<FormLayoutCells>>((acc, row) => {
    row.cells.forEach((cell) => {
      acc[cell.key] = cell;
    });
    return acc;
  }, {});
}

interface ValidateArgs {
  schema: ValidationSchema;
  layout: FormLayoutRow[];
  form: Data<string>;
  touch: Data<boolean>;
  isDirty: boolean;
}
export function validate({ schema, layout, form, touch, isDirty }: ValidateArgs): Validation {
  const { issues = [], success, output } = safeParse(schema, form);
  const cellsByKey = cells({ layout });
  const fields = keys({ layout }).reduce<Data<ValidationField>>((acc, key) => {
    acc[key] = { state: 'idle', issues: [] };
    return acc;
  }, {});

  const isAnswered = (key: string): boolean => {
    const value = form[key] ?? '';
    return cellsByKey[key]?.type === 'checkbox' ? value === 'true' : value !== '';
  };

  issues.forEach(({ path = [], message }) => {
    const key = path.map((p) => p.key).join('');
    if (!fields[key]) return;
    fields[key].issues.push(message);
  });

  const isRequired = (key: string): boolean => {
    const entry = schema.entries[key];
    return entry ? !safeParse(entry, '').success : false;
  };

  const suspicious: string[] = [];

  for (const key in fields) {
    const { issues } = fields[key];
    const revealed = touch[key] || isDirty;
    if (issues.length === 0) {
      fields[key].state = isAnswered(key) ? 'success' : 'idle';
      continue;
    }
    if (issues.some((issue) => !FormSchema.isSuspicious(issue))) {
      fields[key].state = revealed && isRequired(key) ? 'error' : 'idle';
      continue;
    }
    fields[key].state = 'suspicious';
    suspicious.push(key);
  }

  return {
    success,
    hasErrors: Object.values(fields).some((field) => field.issues.some((issue) => !FormSchema.isSuspicious(issue))),
    suspicious,
    output: output as Data<string>,
    fields,
  };
}

interface KeyArgs {
  form: Data<string>;
  key: string;
  fb: string;
}
export function key({ form, key, fb }: KeyArgs): string {
  return form && form[key] ? form[key] : fb;
}
